import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import Radio from "../form-fields/radio";
import apiHelper from "../../utils/apiHelper";
import { ContractorService, LookupService, LookupTypesEnum } from "@propertelligent/client-api";
import InputField from '../form-fields/InputField';
import EmailInput from '../form-fields/EmailInput';
import FancySingleSelectDropdown from '../form-fields/FancyDropDowns/SingleFancyDropdown';
import MultiFancyDropdown from '../form-fields/FancyDropDowns/MultiFancyDropdown';
import RadioGroup from '../form-fields/RadioGroup';
import { GetImages } from '../../utils/GetImages';
import PhoneInput from '../form-fields/PhoneInput';
import { formatLabel } from '../../utils/helper';

interface Option {
  Id: number;
  Name: string;
}

interface ContractorValues {
  serviceType?: string;
  serviceSubType?: string[];
  email: string;
  firstName: string;
  isInHouse: string;
  lastName: string;
  phoneNumber: string;
}

interface AddContractorFormProps {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  fetchData?: (page: number) => void;
}

const defaultValues: ContractorValues = {
  serviceType: '',
  serviceSubType: [],
  email: '',
  firstName: '',
  isInHouse: '1',
  lastName: '',
  phoneNumber: ''
};

const thingToKnow = [{
  points: [
    { text: "The entire property is designed for one household, meaning there are no multiple units within the structure." },
    { text: "The owner has complete control over the property." }
  ]
}];

const AddContractorForm: React.FC<AddContractorFormProps> = ({
  isModalOpen,
  setModalOpen,
  fetchData
}) => {
  const [serviceTypes, setServiceTypes] = useState<Option[]>([]);
  const [serviceSubTypes, setServiceSubTypes] = useState<Option[]>([]);
  const [isSubTypeHidden, setIsSubTypeHidden] = useState(false);
  const [isSubTypeDisabled, setIsSubTypeDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<ContractorValues>({
    defaultValues,
    mode: 'onChange'
  });

  const watchedServiceType = methods.watch("serviceType");

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const serviceTypesData = await fetchLookupData(LookupService.getApiLookup, LookupTypesEnum.ServiceTypes);
      setServiceTypes(serviceTypesData);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchLookupData = useCallback(async (service: any, lookupType: LookupTypesEnum) => {
    const response = await apiHelper(
      service,
      { showNotification: false },
      lookupType
    );
    return response?.data?.success?.map((val: any) => ({
      Id: val.id || val.Id || 0,
      Name: val.description || val.Name || val.name || ''
    })) || [];
  }, []);

  const fetchServiceSubTypes = useCallback(
    debounce(async (value: number) => {
      try {
        setIsSubTypeDisabled(true);
        const response = await apiHelper(
          LookupService.getApiLookup1,
          { showNotification: false },
          LookupTypesEnum.ServiceSubTypes,
          value
        );
        setServiceSubTypes(
          response?.data?.success?.map((item: any) => ({
            Id: item.id || item.Id || 0,
            Name: formatLabel(item.name || item.description || '')
          })) || []
        );
      } catch (error) {
        console.error("Error fetching service sub-types:", error);
      } finally {
        setIsSubTypeDisabled(false);
      }
    }, 300),
    []
  );


  useEffect(() => {
    return () => {
      fetchServiceSubTypes.cancel();
    };
  }, [fetchServiceSubTypes]);

  const handleServiceTypeChange = useCallback((value: any) => {
    const numericValue = Number(value);
    methods.setValue("serviceSubType", []);
    setServiceSubTypes([]);

    const selectedOption = serviceTypes.find(opt => opt.Id === numericValue);
    const typeName = selectedOption?.Name?.toLowerCase() || "";
    const isPlumbingOrElectrical = typeName.includes("plumbing") || typeName.includes("electrical");

    const shouldHide = numericValue === 999 || isPlumbingOrElectrical;
    const shouldDisable = numericValue === 999 || isPlumbingOrElectrical;

    setIsSubTypeHidden(shouldHide);
    setIsSubTypeDisabled(shouldDisable);

    if (!shouldHide && !shouldDisable && value) {
      fetchServiceSubTypes(numericValue);
    }
  }, [fetchServiceSubTypes, methods, serviceTypes]);

  useEffect(() => {
    if (isModalOpen) {
      methods.reset(defaultValues);
      fetchInitialData();
    }
  }, [isModalOpen, fetchInitialData, methods]);

  useEffect(() => {
    if (watchedServiceType) {
      handleServiceTypeChange(watchedServiceType);
    }
  }, [watchedServiceType, handleServiceTypeChange]);

  const onSubmit = useCallback(async (data: ContractorValues) => {
    try {
      setIsLoading(true);
      const requestData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        isInHouse: data.isInHouse === '1',
        ServiceTypesIds: data.serviceType ? [Number(data.serviceType)] : [],
        serviceSubTypesIds: data.serviceSubType?.map(Number) || []
      };

      const response = await apiHelper(
        ContractorService.postApiContractor,
        {
          title: "Contractor Added",
          successMessage: `${data.firstName} ${data.lastName} added successfully`,
          showNotification: true,
          successIcon: <img src={GetImages.toastContractorIcon} alt="success" width="20" height="20" />
        },
        requestData
      );

      if (response.errors) {
        return;
      }
      setModalOpen(false);
      if (fetchData) {
        fetchData(1); // Refresh the list from the first page
      }
    } catch (error: any) {
      console.error("Error adding contractor:", error.response?.data || error.message);
    } finally {
      setIsLoading(false);
    }
  }, [fetchData, setModalOpen]);

  const shouldShowSubTypes = !isSubTypeHidden && watchedServiceType;

  return (
    <RightSideModal
      isOpen={isModalOpen}
      onClose={() => {
        methods.reset(defaultValues);
        setModalOpen(false);
      }}
      title="Add Contractor"
      buttonlabel="Save"
      formId="AddContractorForm"
      maxWidth={550}
      isDirty={methods.formState.isDirty}
      isLoading={methods.formState.isSubmitting}
      isSubmitDisabled={methods.formState.isSubmitting || !methods.formState.isDirty || !methods.formState.isValid}
    >
      <Card className='no-bg'>
        <FormProvider {...methods}>
          <form id="AddContractorForm" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">
                <RadioGroup
                  label="Contractor Type"
                  required
                >
                  <Radio
                    name="isInHouse"
                    value="1"
                    label="In House"
                  />

                  <Radio
                    name="isInHouse"
                    value="0"
                    label="Third Party"
                  />
                </RadioGroup>

              </div>

              <div className="col-md-6">
                <InputField
                  name="firstName"
                  label="First Name"
                  required
                  requiredMessage="First Name is required"
                  type="text"
                  placeholder="Enter First Name"
                  onlyAlphabets={true}
                  validation={{
                    minLength: {
                      value: 2, message: "First name must be at least 2 characters long"
                    },
                    maxLength: {
                      value: 20, message: "First name cannot exceed 20 characters"
                    }
                  }}
                />
              </div>
              <div className="col-md-6">
                <InputField
                  name="lastName"
                  label="Last Name"
                  required
                  requiredMessage="Last Name is required"
                  type="text"
                  placeholder="Enter Last Name"
                  onlyAlphabets={true}
                  validation={{
                    minLength: {
                      value: 2, message: "Last name must be at least 2 characters long"
                    },
                    maxLength: {
                      value: 20, message: "Last name cannot exceed 20 characters"
                    }
                  }}
                />
              </div>
              <div className="col-md-6">
                <EmailInput
                  name="email"
                  label="Email Address"
                  required
                  requiredMessage="Email is required"
                  placeholder="Enter Email Address"
                />
              </div>
              <div className="col-md-6">
                <PhoneInput
                  name="phoneNumber"
                  label="Contact No"
                  required
                />
              </div>

              <div className="col-md-12">
                <FancySingleSelectDropdown
                  name="serviceType"
                  label="Contractor Type"
                  required
                  requiredMessage="Contractor Type is required"
                  options={serviceTypes}
                  onChange={handleServiceTypeChange}
                  disabled={isLoading}

                />
              </div>

              {shouldShowSubTypes && (
                <div className="col-md-12">
                  <MultiFancyDropdown
                    name="serviceSubType"
                    label="Contractor Sub Type"
                    required={!isSubTypeDisabled && !isSubTypeHidden}
                    requiredMessage="Contractor Sub Type is required"
                    options={serviceSubTypes}
                    disabled={isSubTypeDisabled || isSubTypeHidden || isLoading}
                  />

                </div>
              )}
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default React.memo(AddContractorForm);